import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Box from '@material-ui/core/Box';
import FormHelperText from '@material-ui/core/FormHelperText';
import { showAlert } from 'actions/app';
import API from 'services/API';
import strings from 'strings';
import TextBox from 'UI/components/atoms/TextBox';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FPTextEditor from 'UI/components/molecules/FPTextEditor';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { tinymceConfig } from 'UI/constants/config';
import { DateFormats } from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { getErrorMessage, toLocalTime } from 'UI/utils';

import { useStyles } from './styles';

const DRAWER_ACTIONS = {
  update: 'Updated',
  create: 'Created'
};

/**
 * @param {Object} props
 * @param {string} props.endpoint
 * @param {*} props.selectedItem
 * @param {Function} props.onNoteCompleted
 * @param {Function} props.onNoteClosed
 * @param {string} props.itemName
 * @param {Object} props.initialValues
 * @param {boolean} props.isQuickViewMode
 * @param {React.ReactNode} [props.topContent]
 */
const NoteForm = ({
  endpoint,
  extraPayloadData = {},
  FPDrawerProps,
  initialValues,
  isQuickViewMode,
  itemName,
  onNoteClosed,
  onNoteCompleted,
  selectedItem,
  topContent = null
}) => {
  const editorRef = useRef(null);
  const classes = useStyles();
  const dispatch = useDispatch();

  const isEditing = !!selectedItem?.id;

  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false,
    isEditing,
    isFormDisabled: false,
    isReadOnly: false
  });

  const localTime = selectedItem?.created_at && toLocalTime(selectedItem.created_at);
  const onlyCreationDate = localTime && localTime.format(DateFormats.SimpleDate);
  const onlyCreationTime = localTime && localTime.format(DateFormats.SimpleTime);

  const finalEndpoint = useMemo(() => `${endpoint}/${Endpoints.Notes}`, [endpoint]);

  const { register, errors, handleSubmit, setValue, watch } = useForm(
    selectedItem ? { defaultValues: { ...selectedItem } } : { defaultValues: initialValues }
  );
  const watchers = watch(['body']);

  useEffect(() => {
    register(
      { name: 'body' },
      { required: strings.inventoryProfiles.sections.tabs.activityNote.form.validation.note }
    );
  }, [register]);

  const handleEditorChange = editorValue => setValue('body', editorValue, true);

  const saveForm = async formData => {
    setUiState(prevState => ({ ...prevState, isSaving: true }));
    await saveNote(formData);
  };

  const saveNote = async note => {
    try {
      const response = !uiState.isEditing
        ? await API.post(finalEndpoint, { ...note, ...extraPayloadData })
        : await API.put(`${finalEndpoint}/${selectedItem.id}`, note);

      if (response.data) {
        dispatch(
          showAlert({
            severity: 'success',
            title: strings.formatString(
              strings.inventoryProfiles.sections.tabs.activityNote.creationAction,
              {
                section: 'Note',
                action: uiState.isEditing ? DRAWER_ACTIONS.update : DRAWER_ACTIONS.create
              }
            ),
            body: itemName || ''
          })
        );

        onNoteCompleted && onNoteCompleted(!uiState.isEditing ? response.data.data : response.data);
      }
    } catch (error) {
      dispatch(
        showAlert({
          severity: 'error',
          title: 'Error',
          body: getErrorMessage(error)
        })
      );
      setUiState(prevState => ({
        ...prevState,
        isSuccess: false,
        isSaving: false
      }));
    }
  };

  const initEditor = (e, editor) => {
    editorRef.current = editor;
  };

  const drawerCopies = {
    button: isQuickViewMode
      ? strings.inventoryProfiles.sections.tabs.activityNote.drawer.backAction
      : strings.shared.ui.close,
    title: strings.inventoryProfiles.sections.tabs.activityNote.drawer.title.note
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onNoteClosed
      },
      GridItemProps: {
        md: 3
      }
    },
    {
      props: {
        initialText: strings.shared.ui.save,
        variant: 'contained',
        type: 'submit',
        disabled: uiState.isSaving,
        onProgressText: strings.shared.ui.saving,
        isSaving: uiState.isSaving
      },
      GridItemProps: {
        md: 3
      },
      mode: 'save'
    }
  ];

  return (
    <FPDrawer
      CardHeaderProps={{ title: drawerCopies.title }}
      ContentWrapper="form"
      ContentWrapperProps={{ onSubmit: handleSubmit(saveForm) }}
      FPDrawerProps={FPDrawerProps}
      onClose={onNoteClosed}
      footer={
        <FPButtonBuilder
          GridContainerProps={{ spacing: 2 }}
          buttonDefinitions={buttonDefinitions}
        />
      }
    >
      <div className={classes.root}>
        <Box mt={2} mb={3}>
          {topContent}
          <TextBox
            name="title"
            label="Title *"
            error={!!errors.title}
            errorText={errors.title && errors.title.message}
            inputRef={register({
              required: strings.inventoryProfiles.sections.tabs.activityNote.form.validation.title
            })}
            disabled={uiState.isFormDisabled}
          />
        </Box>
        {uiState.isFormDisabled || (
          <FPTextEditor
            onInit={initEditor}
            config={tinymceConfig}
            value={watchers.body}
            onChange={handleEditorChange}
            disabled={uiState.isFormDisabled}
            error={!!errors.body}
            errorText={errors.body && errors.body.message}
          />
        )}
        {selectedItem?.user && (
          <FormHelperText className={classes.helper}>
            Created by{' '}
            <b>
              {selectedItem?.user.personalInformation?.full_name} - {selectedItem?.user.email}
            </b>
            {onlyCreationDate && (
              <>
                on <b>{onlyCreationDate}</b> at <b>{onlyCreationTime}</b>
              </>
            )}
          </FormHelperText>
        )}
      </div>
    </FPDrawer>
  );
};

export default NoteForm;
